import {
  type DeepPartial,
  type Product,
  type ProductType,
} from "@jasper/client-models";
import { productWorkflowConfigurations } from "./configurations";
import {
  TeethSelectionEnum,
  type ProductWorkflowConfiguration,
  type WorkflowProduct,
} from "./configurations/ProductWorkflowConfiguration";
import { type StepKeys } from "../steps/types";

export * from "./configurations/types";

const getProductConfiguration = (
  productType: ProductType
): ProductWorkflowConfiguration<Product, StepKeys> => {
  const configuration = productWorkflowConfigurations[productType];
  if (!configuration) {
    throw new Error(`No configuration found for product type: ${productType}`);
  }

  return configuration;
};

export const getStepsForProductType = (product: WorkflowProduct) => {
  const configuration = getProductConfiguration(product.type);
  return configuration.steps;
};

export const getStepsForProduct = (product: WorkflowProduct) => {
  if (!product.type) {
    throw new Error("Product type is required to get workflow steps");
  }

  return getStepsForProductType(product);
};

export const getCompletedSteps = (product: DeepPartial<Product>) => {
  if (!product.type) {
    throw new Error("Product type is required to get completed steps");
  }

  const configuration = getProductConfiguration(product.type);
  const completedSteps = configuration.getCompletedSteps(product);
  return completedSteps;
};

export const getItemTypeForProductType = (productType: ProductType) => {
  const configuration = getProductConfiguration(productType);
  return configuration.itemType;
};

export const getTeethSelectionForProductType = (productType: ProductType) => {
  const configuration = getProductConfiguration(productType);
  return configuration.teethSelection;
};

export const isBulkCreationSupportedForProductType = (
  productType: ProductType
) => {
  const configuration = getProductConfiguration(productType);
  return configuration.isBulkSupported;
};

export const isArchScopedProductType = (productType: ProductType) => {
  const configuration = getProductConfiguration(productType);
  return configuration.teethSelection === TeethSelectionEnum.ARCH;
};

export const isBulkCompatible = (
  leaderProduct: Product,
  followerProduct: Product
) => {
  const configuration = getProductConfiguration(leaderProduct.type);

  if (!configuration.isBulkCompatible) {
    return leaderProduct.type === followerProduct.type;
  }

  return configuration.isBulkCompatible(leaderProduct, followerProduct);
};

export const isValidProduct = (product: DeepPartial<Product>) => {
  if (!product.type) {
    return false;
  }

  try {
    const configuration = getProductConfiguration(product.type);
    return configuration.isValidProduct(product);
  } catch {
    return false;
  }
};
